import {
  EDatasetColumn,
  TCsvSeparator,
  TDatasetColumnType,
  TDatasetProcessingErrorType,
  TDecimalSeparator,
} from 'FeatureModules/DataImport/types/dataset.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { imageSchema } from 'utils/sharedSchema.utils';
import * as Yup from 'yup';

export type IDatasetFormValue = {
  // This only exists if the dataset was already created in the backend
  datasetId?: string;
  title: string;
  rawMaterialId: string;
  countryCode: string;
  ownedBy: IBasePartnerCompany;
};

export type IGeoUploadFormValues = IDatasetFormValue & {
  /**
   * This is used to keep track of the files that were already uploaded and attached to the datasets and are not of the upload context
   */
  files: {
    id: string;
    title: string;
    inUploadContext: boolean;
    processingError?: { message: string; type?: TDatasetProcessingErrorType };
  }[];
  fileConfiguration: {
    csvSeparator?: TCsvSeparator;
    decimalSeparator?: TDecimalSeparator;
    autoCloseGeojsonGeometries?: boolean;
  };
};

export interface IDatasetImportFormValues {
  overwriteExisting: boolean;
}

export interface IDatasetFarmClusteringFormValues {
  clusterSites: boolean;
  siteClusterId: string | null;
  siteClusterTitle: string | null;
}

export interface IColumnMappingValues {
  targetColumnName: TDatasetColumnType;
  sourceColumnName: string;
  columnDataExample?: string[];
  removed?: boolean;
}

export interface IGeoMappingFormValues {
  datasetId: string;
  mappedColumns: IColumnMappingValues[];
}

export const datasetImportSchema = Yup.object().shape({
  overwriteExisting: Yup.boolean().default(true),
}) as Yup.ObjectSchema<IDatasetImportFormValues>;

export const datasetFarmClusteringSchema = Yup.object().shape({
  clusterSites: Yup.boolean().default(true),
  siteClusterId: Yup.string().when('clusterSites', {
    is: true,
    then: Yup.string().required('Select an option if you want cluster all the farms'),
    otherwise: Yup.string().nullable(),
  }),
  siteClusterTitle: Yup.string().when('siteClusterId', {
    is: 'new-cluster',
    then: Yup.string().required('Please provide a name for the new farm cluster'),
    otherwise: Yup.string().nullable(),
  }),
}) as Yup.ObjectSchema<IDatasetFarmClusteringFormValues>;

export const datasetSchema = Yup.object().shape({
  datasetId: Yup.string().nullable(),
  title: Yup.string().default('').nullable(false).required('Please enter a title'),
  rawMaterialId: Yup.string().required('Please select a raw material'),
  countryCode: Yup.string().required('Please select a country of origin'),
  ownedBy: Yup.object()
    .shape({ id: Yup.string(), name: Yup.string(), logo: imageSchema })
    .default(undefined)
    .required('Please select a partner'),
}) as Yup.ObjectSchema<IDatasetFormValue>;

export const geoUploadDatasetSchema = Yup.object().shape({
  ...datasetSchema.fields,
  files: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string(),
        title: Yup.string(),
        inUploadContext: Yup.boolean(),
      })
    )
    .min(1)
    .default([]),
  fileConfiguration: Yup.object().shape({
    csvSeparator: Yup.string().nullable(),
    decimalSeparator: Yup.string().nullable(),
  }),
}) as Yup.ObjectSchema<IGeoUploadFormValues>;

export const mappedColumnsSchema = Yup.object().shape({
  targetColumnName: Yup.string(),
  sourceColumnName: Yup.string(),
  columnDataExample: Yup.array().of(Yup.string()),
  removed: Yup.boolean().default(false),
}) as Yup.ObjectSchema<IColumnMappingValues>;

export const geoMappingDatasetSchema = Yup.object().shape({
  datasetId: Yup.string(),
  mappedColumns: Yup.array().of(mappedColumnsSchema),
}) as Yup.ObjectSchema<IGeoMappingFormValues>;

/**
 * Custom validation function to check the mapping of the columns
 */
export const geoDataCustomValidation = (values: IGeoMappingFormValues) => {
  const mappedColumns = values.mappedColumns?.filter(column => !column.removed) || [];

  // Check if all columns are filled or removed
  if (!mappedColumns.every(({ targetColumnName, removed }) => targetColumnName || removed)) {
    return {
      mappedColumns: [
        'Please map each of your sheet columns to one of our template columns or exclude any columns that are not needed.',
      ],
    };
  }

  // Check if all required columns exist
  if (
    !mappedColumns.some(
      ({ targetColumnName }) =>
        targetColumnName === EDatasetColumn.LATITUDE ||
        targetColumnName === EDatasetColumn.LONGITUDE ||
        targetColumnName === EDatasetColumn.COORDINATES_LAT_LNG ||
        targetColumnName === EDatasetColumn.COORDINATES_LNG_LAT ||
        targetColumnName === EDatasetColumn.GEOMETRY_WKT
    )
  ) {
    return {
      mappedColumns: [
        'Please make sure you have mapped the required geometry column (Latitude and Longitude, Coordinates (lat, lng), Coordinates (lng, lat) or WKT Geometry selected.',
      ],
    };
  }

  // If targetColumnName EDatasetColumn.LATITUDE or EDatasetColumn.LONGITUDE is selected make sure the other one is also selected
  const hasSingleCoordinates = mappedColumns.some(
    ({ targetColumnName }) =>
      targetColumnName === EDatasetColumn.LATITUDE || targetColumnName === EDatasetColumn.LONGITUDE
  );
  if (
    hasSingleCoordinates &&
    (!mappedColumns.some(({ targetColumnName }) => targetColumnName === EDatasetColumn.LATITUDE) ||
      !mappedColumns.some(({ targetColumnName }) => targetColumnName === EDatasetColumn.LONGITUDE))
  ) {
    return {
      mappedColumns: [
        'Please make sure you have selected both Latitude and Longitude columns for your plot.',
      ],
    };
  }

  // Make sure only one of the geometry columns is selected
  if (
    mappedColumns.filter(
      ({ targetColumnName }) =>
        targetColumnName === EDatasetColumn.LATITUDE ||
        targetColumnName === EDatasetColumn.COORDINATES_LAT_LNG ||
        targetColumnName === EDatasetColumn.COORDINATES_LNG_LAT ||
        targetColumnName === EDatasetColumn.GEOMETRY_WKT
    ).length > 1
  ) {
    return {
      mappedColumns: [
        'Please make sure you have selected only one of the geometry columns for your plot (Latitude and Longitude, Coordinates (lat, lng), Coordinates (lng, lat) or WKT Geometry).',
      ],
    };
  }

  // Check if no duplicates exist
  if (
    mappedColumns.some(
      ({ targetColumnName }) =>
        mappedColumns.filter(column => column.targetColumnName === targetColumnName).length > 1
    )
  ) {
    return {
      mappedColumns: ['Please make sure you have not selected the same column more than once.'],
    };
  }

  return {};
};
